import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import useAutoOpenAdd from "../common/useAutoOpenAdd";
import OldMedicines from "./OldMedicines";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import { medicineBand } from "./data";
import MedicineService from "./MedicineService";
import MetaTooltip from "./MetaTooltip";
import MobileReadMore, { useMobileReadMore } from "mobile/components/MobileReadMore";
import { fetchSaltAllergies, invalidateSaltAllergies } from "./saltAllergyCache";
import { ALG_V2_REFRESH } from "../allergies/events";
import TrendModal from "./TrendModal";
import { MED_V2_REFRESH, MED_V2_ADD, MED_V2_STATUS, MED_V2_ADHERENCE, MED_V2_EDIT_CAPTURED } from "./events";
import { SECTION_SELECT } from "../railEvents";
import MedicinesEmpty from "./MedicinesEmpty";

/**
 * Medicines — content column, px-matched to Figma 2964:4139 (`Content Host`):
 * the drug-allergies bar, the hero table with two-line rows, and the
 * past-medicines bar, all in the shared three-column shell. The write surface
 * lives in the editor rail (MedicinesEditor); this column only announces the
 * intent via MED_V2_ADD.
 */

// Every persisted medicine has at least one StatusEntry (the initial add),
// so a single-row trend is a real story worth showing. Only skip when the
// audit is genuinely empty (defensive — shouldn't happen for a saved row).
const hasTrend = (m) => Array.isArray(m.statusHistory) && m.statusHistory.length >= 1;

// "Ended on" for the past medicines table.
//
// Wave-2 moved to the "regimen period = StatusEntry" model: a terminate
// no longer appends a new row — it mutates the live entry in place
// (status flipped, UpdatedAt/UpdatedBy stamped, SinceWhen untouched).
// That means `sinceWhen` on the live entry is still the regimen's
// Add-time stamp (= medStartDate), which is the WRONG thing to show
// under an "Ended on" header.
//
// Correct source per status, taken from the latest non-Ongoing,
// non-Delete entry:
//   • Discontinued → the reviewer-picked DiscontinuedDate (same value
//     the Update card + trend's "Discontinued on" column render).
//   • Completed    → the scheduled end date: medEndDate on the entry,
//     or derived as medStartDate + durationDays − 1 when the record
//     doesn't carry one (ZY-10312 "start = day 1" convention).
//   • Fallback     → the entry's UpdatedAt (legacy rows pre-wave-2
//     that still appended a terminator row stamp UpdatedAt only on the
//     new entry; this also handles rows mid-migration).
//   • Last resort  → m.endedAt, the wall-clock save time stamped on
//     the medicine doc at termination.
const endedSinceWhen = (m) => {
  const hist = Array.isArray(m && m.statusHistory) ? m.statusHistory : [];
  for (let i = hist.length - 1; i >= 0; i -= 1) {
    const e = hist[i];
    if (!e || e.event === "delete") continue;
    if (!e.status || e.status === "ongoing") continue;
    if (e.status === "discontinued" && e.discontinuedDate) {
      return e.discontinuedDate;
    }
    if (e.status === "completed") {
      if (e.medEndDate) return e.medEndDate;
      if (e.medStartDate && e.durationDays > 0) {
        const derived = addDaysISO(e.medStartDate, e.durationDays - 1);
        if (derived) return derived;
      }
    }
    return e.updatedAt || e.sinceWhen || (m && m.endedAt);
  }
  return m && m.endedAt;
};

// Adherence icon color for a medicine row. Rules (locked with product):
//   • Green — the latest StatusEntry has one or more AdherenceReviews.
//   • Red   — the medicine is Completed / Discontinued AND no review yet
//             on the latest entry (review is required).
//   • Gray  — everything else (Ongoing with no review yet).
// Any new Update appends a fresh StatusEntry whose adherenceReviews is
// empty, so the icon resets to Gray (or Red) after every regimen change.
const adherenceIconState = (m) => {
  const hist = (m && m.statusHistory) || [];
  const last = hist.length > 0 ? hist[hist.length - 1] : null;
  const reviews = (last && last.adherenceReviews) || [];
  if (reviews.length > 0) return "green";
  if (m && (m.status === "completed" || m.status === "discontinued")) return "red";
  return "gray";
};

const ADHERENCE_TITLE = {
  gray: "Add adherence review",
  red: "Adherence review required",
  green: "Add another adherence review",
};

// Tri-state adherence icon button. State is derived from the medicine's
// latest StatusEntry so it self-heals across list refreshes.
const AdherenceButton = ({ medicine, onOpen }) => {
  const state = adherenceIconState(medicine);
  return (
    <button
      type="button"
      className={`med3-adherence is-${state}`}
      onClick={() => onOpen(medicine)}
      title={ADHERENCE_TITLE[state]}
      aria-label={ADHERENCE_TITLE[state]}
    >
      <FactCheckIcon />
    </button>
  );
};
AdherenceButton.propTypes = {
  medicine: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
};

// Human label for each slot tag — named slots pass through; the special
// "asNeeded" tag rewrites to "As needed" so the column reads naturally.
const slotLabel = (s) => (s === "asNeeded" ? "As needed" : s);

// Merged Frequency (Dose qty.) cell — one column carries BOTH the cadence
// and the per-slot / scalar quantity, in the shape the mock uses:
//   per-slot          → "Morning (2 unit), Night (4 unit)"
//   named slots list  → "Morning, Night (2 unit)"    ← qty trails once
//   as-needed         → "As needed (2 unit)"
//   custom cadence    → "Every 2 hrs (1 ml)"
//   dose only         → "1 ml"
//   nothing recorded  → "—"
const frequencyDoseText = (m) => {
  const cur = m.current || {};
  const dq = cur.doseQuantity || {};
  const per = cur.perSlotQuantities || {};
  const f = cur.doseFrequency || {};
  const hasPerSlot = per && Object.keys(per).length > 0;
  const asNeededSlot =
    Array.isArray(f.timesOfDay) && f.timesOfDay.includes("asNeeded");

  // "(1 ml)" formatter. Empty when both value + unit missing.
  const parens = (v, u) => {
    const t = `${v || ""} ${u || ""}`.trim();
    return t ? ` (${t})` : "";
  };

  if (hasPerSlot) {
    const order = ["Morning", "Afternoon", "Evening", "Night"];
    const parts = [];
    order.forEach((slot) => {
      const q = per[slot];
      if (!q) return;
      parts.push(`${slot}${parens(q.value, q.unit)}`);
    });
    if (parts.length > 0) return parts.join(", ");
  }

  // The forms stopped writing `interval` (chips now fill the custom pair),
  // but the model still stores it, so older records carry only "6h".
  const rawInterval = String(f.interval || "").trim();
  const hourly = rawInterval.match(/^(\d+)\s*h(?:ours?|rs?)?$/i);
  const intervalText = hourly ? `Every ${hourly[1]} hrs` : rawInterval;

  if (asNeededSlot) return `As needed${parens(dq.value, dq.unit)}`;

  if (Array.isArray(f.timesOfDay) && f.timesOfDay.length > 0) {
    const cadence = intervalText ? ` · ${intervalText}` : "";
    return `${f.timesOfDay.map(slotLabel).join(", ")}${cadence}${parens(dq.value, dq.unit)}`;
  }

  if (f.customValue && f.customUnit) {
    const abbrev = { Hours: "hrs", Days: "days", Weeks: "wks", Months: "mos" }[f.customUnit] || f.customUnit;
    return `Every ${f.customValue} ${abbrev}${parens(dq.value, dq.unit)}`;
  }

  if (intervalText) return `${intervalText}${parens(dq.value, dq.unit)}`;

  const scalar = `${dq.value || ""} ${dq.unit || ""}`.trim();
  return scalar || "—";
};

const INSTR_LABELS = {
  before: "Before meal",
  with: "With meal",
  after: "After meal",
  "after-dinner": "After dinner",
  empty: "Empty stomach",
  local: "Local Application",
};

/* Board 3433:2879 — the meta line: "14 May – 20 May 2026 · 7 days · <note>",
   or "10 Feb – Ongoing · <note>". The start drops its year when the end
   carries the same one; the day count is omitted for ongoing courses. */
const dayMonth = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};
// Compute an end date from a start + whole-day duration when the record
// doesn't carry one. Mirrors MedicineForm's `addDays` and the ZY-10312
// "start date is day 1" convention — the end is `start + duration - 1`.
// Null on bad input so the caller can fall through cleanly.
const addDaysISO = (isoDate, days) => {
  const d = new Date(isoDate);
  if (isNaN(d.getTime()) || !days) return null;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const metaText = (m) => {
  const cur = m.current || {};
  const parts = [];
  if (cur.medStartDate) {
    // ZY-10352 — the meta line should always read "<start> – <end> · <N>
    // days" for a bounded regimen, even if the backend didn't store
    // medEndDate. Derive it from start + duration (ZY-10312 convention:
    // start = day 1, so endDate = startDate + durationDays - 1) when
    // missing. Ongoing-indefinite still renders as "<start> – Ongoing".
    const effectiveEndISO = cur.ongoingIndefinitely
      ? null
      : cur.medEndDate
        ? cur.medEndDate
        : (cur.durationDays > 0 ? addDaysISO(cur.medStartDate, cur.durationDays - 1) : null);
    const startYear = new Date(cur.medStartDate).getFullYear();
    // The year the reader would assume anyway: the end's, or — for an
    // ongoing course with no end — the current one ("10 Feb – Ongoing").
    const endYear = effectiveEndISO
      ? new Date(effectiveEndISO).getFullYear()
      : new Date().getFullYear();
    const start = endYear === startYear ? dayMonth(cur.medStartDate) : formatShortDate(cur.medStartDate);
    const end = cur.ongoingIndefinitely
      ? "Ongoing"
      : effectiveEndISO
      ? formatShortDate(effectiveEndISO)
      : null;
    parts.push(end ? `${start} – ${end}` : start);
  }
  if (!cur.ongoingIndefinitely && cur.durationDays > 0) {
    parts.push(`${cur.durationDays} day${cur.durationDays === 1 ? "" : "s"}`);
  }
  if (cur.description) parts.push(cur.description);
  return parts.join(" · ");
};

const MedicineName = ({ medicine, formulation, formulationName }) => {
  const label = formulationName || formulation;
  return (
    <>
      {medicine}
      {label ? <span className="med3-form"> ({label})</span> : null}
    </>
  );
};
MedicineName.propTypes = {
  medicine: PropTypes.string.isRequired,
  formulation: PropTypes.string,
  formulationName: PropTypes.string,
};

/* Board 3447:6355 / I302:3149 — the chip's 16px stroked chevron, inheriting
   the band's own ink. */
const ChipCaret = () => (
  <svg viewBox="0 0 11.33 6.67" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M10.33 1L5.67 5.67L1 1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Board 3504:2861 — Material `fact_check` at 16px. Material Icons is not
   loaded in this app, so the official 24dp path is inlined. */
const FactCheckIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM19.41 10.42 17.99 9l-3.17 3.17-1.41-1.42-1.42 1.42L14.82 15l4.59-4.58zM5 7h5v2H5V7zm0 4h5v2H5v-2zm0 4h5v2H5v-2z" />
  </svg>
);

/* Board 3507:2866 — Material `open_in_new` at 14px, same inlining rule. */
const OpenInNewIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
  </svg>
);


/* The standard v3 toolbar plus (11px 1.4-stroke vector). */
const PlusIcon = () => (
  <svg viewBox="0 0 12.4 12.4" width="11" height="11" fill="none" aria-hidden="true">
    <path
      d="M6.2 0.7V11.7M0.7 6.2H11.7"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StatusChip = ({ status, onClick, readOnly }) => {
  const band = medicineBand(status || "ongoing");
  // Transcript mode on persisted rows renders the chip as a plain span
  // (no button, no caret, no update tooltip) — update flows are
  // disabled this phase so there is nothing to click into.
  if (readOnly) {
    return (
      <span
        className="med3-chipbtn is-readonly"
        style={{ background: band.bg, color: band.fg }}
      >
        <span className="med3-chipbtn-dot" style={{ background: band.dot }} aria-hidden="true" />
        <span className="med3-chipbtn-label">{band.label}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      className="med3-chipbtn"
      style={{ background: band.bg, color: band.fg }}
      onClick={onClick}
      title="Update status"
    >
      <span className="med3-chipbtn-dot" style={{ background: band.dot }} aria-hidden="true" />
      <span className="med3-chipbtn-label">{band.label}</span>
      <ChipCaret />
    </button>
  );
};
StatusChip.propTypes = {
  status: PropTypes.string,
  onClick: PropTypes.func,
  readOnly: PropTypes.bool,
};
StatusChip.defaultProps = {
  onClick: undefined,
  readOnly: false,
};

// Build a synthetic Medicine-shaped row from a staged "add" captured item
// so it can slot into the same table as Med v2 rows. Mirrors CC's
// stagedAddToRow / Dx's stagedAddToDxRow. Wave A first-cut: staged UPDATES
// aren't rendered (no update auto-route yet — every AI-added med is a
// fresh add), so no updatesByEntryId map / withEffectiveMed needed.
// Days between two YYYY-MM-DD dates, inclusive of both endpoints — matches
// metaText's ZY-10312 "start = day 1" convention so a 1-Oct→7-Oct span
// reads "7 days" like the standalone Med v2 record does.
const inclusiveDaySpan = (startIso, endIso) => {
  if (!startIso || !endIso) return 0;
  const s = new Date(startIso);
  const e = new Date(endIso);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return 0;
  const ms = e.getTime() - s.getTime();
  const days = Math.round(ms / 86400000) + 1;
  return days > 0 ? days : 0;
};

const stagedAddToMedRow = (item) => {
  const p = (item && item.current) || {};
  // bodyToCapturedAdd stores status="ongoing" when the reviewer flagged
  // the regimen as lifelong (ongoing-indefinite); scheduled regimens
  // carry an explicit end_date instead. Fall back to the text
  // time_period for AI emits that pre-date the status field.
  const ongoingIndefinitely = p.status === "ongoing"
    || (typeof p.time_period === "string" && p.time_period.toLowerCase() === "lifelong");
  const medStartDate = p.start_date || p.since_date || null;
  const medEndDate = ongoingIndefinitely ? null : (p.end_date || null);
  return {
    medicineId:  `staged-${item.id}`,
    medicine:    p.medicine || p.name || p.value || "(untitled)",
    formulation: p.formulation || "",
    status:      p.status || "ongoing",
    statusHistory: [],
    current: {
      medStartDate,
      medEndDate,
      ongoingIndefinitely,
      // Needed for metaText's "<start> – <end> · <N> days" line to
      // match standalone/Orbit. Derived from start/end so the
      // adapter doesn't need a structured siblng today — if end is
      // missing the day count silently drops, same as a Med v2 row
      // with no durationDays.
      durationDays: ongoingIndefinitely ? 0 : inclusiveDaySpan(medStartDate, medEndDate),
      description:   p.description || "",
      instructions:  p.instructions || "",
      // Carry the structured dose/frequency through from the captured
      // payload (bodyToCapturedAdd writes dose_quantity /
      // per_slot_quantities / dose_frequency alongside the text
      // summaries) so the left-side table's "Frequency (Dose qty.)"
      // cell can render the reviewer's actual pick instead of "—".
      doseQuantity:      p.dose_quantity      || { value: "", unit: "" },
      doseFrequency:     p.dose_frequency     || { timesOfDay: [], customValue: null, customUnit: "" },
      perSlotQuantities: p.per_slot_quantities || {},
    },
    _captured:     item,
    _capturedIsAi: item.source === "ai",
    _isRejected:   item.state === "rejected",
    _stagedAdd:    true,
  };
};

/**
 * Old / New. NEW is this v2 section. OLD is the legacy medication-tracking
 * feed, read-only — a different data model entirely (dated snapshots of the
 * whole list, with no per-medicine identity), which is why it is a separate
 * component rather than a filter over these rows.
 */
const VIEW_NEW = "New";
const VIEW_OLD = "Old";

const Medicines = ({ patient, capturedItems, autoOpenAdd, onAutoOpenAdd, onRowsSync }) => {
  const patientId = patient && (patient.id || patient.patientID);
  const [view, setView] = useState(VIEW_NEW);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pastOpen, setPastOpen] = useState(false);
  const [trendTarget, setTrendTarget] = useState(null);
  // Names only — the bar is a pointer to the Allergies section, not a copy
  // of it, so it reads the list and nothing else.
  const [saltAllergies, setSaltAllergies] = useState([]);

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new MedicineService();
    svc.list(
      patientId,
      (res) => {
        const next = res || { active: [], past: [], version: 0 };
        setDoc(next);
        setLoading(false);
        // Transcript-mode AI-match-to-DB dedup: CapturedSections uses
        // this list to detect when an AI proposal's medicine name
        // exactly matches an existing Med v2 row and opens the match
        // as an update card instead of a duplicate add. Note Med v2
        // rows carry mCode as their identity but AI proposals only
        // carry ATC + free-text name — mCode isn't on either side of
        // the match, so dedup here falls back to case-insensitive
        // name matching. Mirrors ChiefComplaints / Diagnosis /
        // Allergies / FamilyHistory / PhysicalActivity / Hosp.
        if (typeof onRowsSync === "function") {
          const rows = [...(next.active || []), ...(next.past || [])];
          onRowsSync(rows);
        }
      },
      (err) => {
        setError((err && err.message) || "Failed to load medicines");
        setLoading(false);
      }
    );
  }, [patientId, onRowsSync]);

  useEffect(() => {
    load();
    EventService.addListener(MED_V2_REFRESH, load);
    return () => EventService.removeListener(MED_V2_REFRESH, load);
  }, [load]);

  // Board 3507:2860 — the bar lists the patient's SALT-category allergies.
  // Reads via the shared saltAllergyCache so the pick-time guard in
  // MedicineForm hits the same list without a second AllergyService call.
  // A failed read yields [] rather than erroring the section — the
  // medicines table must not be hostage to the allergy service.
  //
  // Three refresh triggers wired to keep the strip and the pick-time
  // guard in sync with the Allergies section:
  //
  //   1. Section mount — invalidate the cache first, so a menu switch
  //      that unmounts + remounts this section (or a hard page reload
  //      of the Medicines route) always re-hits AllergyService.list
  //      instead of resolving from a warm-but-stale cache entry. This
  //      is the "force check on every Medicines menu select" the
  //      reviewer explicitly asked for.
  //   2. ALG_V2_REFRESH — fires on every Allergies mutation while THIS
  //      section is mounted. Same invalidate + refetch step so the
  //      strip updates without needing a menu switch.
  //   3. Global SECTION_SELECT to "medicines" — fires when the
  //      section stays mounted but is re-selected from the sidebar
  //      (some layouts keep siblings mounted for scroll continuity),
  //      so the strip refreshes on the re-entry.
  useEffect(() => {
    if (!patientId) return undefined;
    let alive = true;
    const refresh = () => {
      invalidateSaltAllergies(patientId);
      fetchSaltAllergies(patientId).then((rows) => {
        if (alive) setSaltAllergies(rows);
      });
    };
    // Trigger #1 — mount-time force fetch.
    refresh();
    // Trigger #2 — Allergies-mutation broadcast.
    EventService.addListener(ALG_V2_REFRESH, refresh);
    // Trigger #3 — section re-entry from the sidebar. `SECTION_SELECT`
    // is emitted with the target section id ("medicines" here).
    const onSectionSelect = (target) => {
      if (target === "medicines") refresh();
    };
    EventService.addListener(SECTION_SELECT, onSectionSelect);
    return () => {
      alive = false;
      EventService.removeListener(ALG_V2_REFRESH, refresh);
      EventService.removeListener(SECTION_SELECT, onSectionSelect);
    };
  }, [patientId]);

  const openAdd = () => EventService.emit(MED_V2_ADD);
  useAutoOpenAdd({ autoOpenAdd, onAutoOpenAdd, loading, error, doc, open: openAdd });
  /* MOBILE ONLY: the past description cut at 5 lines with "Read more" — its
     `.sec-clamp` lifts on hover alone. The active meta line gets the same
     through MetaTooltip. */
  const readMore = useMobileReadMore();
  // The Update sheet lives in the same rail; the marker routes it there.
  // Transcript-mode captured rows (peach) route to a re-edit event instead
  // so the modal's CapturedSections can pop the AI proposal back open as
  // an editable card — the standalone Update sheet would land in the
  // wrong rail (patient-page editor) once the modal is open.
  const openStatus = (m) => {
    if (m && m._captured) {
      EventService.emit(MED_V2_EDIT_CAPTURED, {
        captured: m._captured,
        medv2Record: m._stagedAdd ? null : m,
      });
      return;
    }
    EventService.emit(MED_V2_STATUS, { medicine: m, mode: "status" });
  };
  // Adherence-review sheet — always opens blank, never dedups. Clicking a
  // green (already-reviewed) icon is a request for ANOTHER review.
  const openAdherence = (m) =>
    EventService.emit(MED_V2_ADHERENCE, { medicine: m, mode: "adherence" });

  const activeRaw = (doc && doc.active) || [];
  const pastRaw = (doc && doc.past) || [];

  // Transcript-mode overlay. Wave A first-cut: only staged ADDS are merged
  // (staged updates would need auto-route on name/mCode match — that's a
  // Wave A pass-2 follow-up). Row visual: `is-captured` → peach bg;
  // `_capturedIsAi` → sparkle icon before the medicine name.
  const inTranscriptMode = !!(capturedItems && capturedItems.length > 0);
  const stagedAddRows = [];
  if (inTranscriptMode) {
    for (const it of capturedItems) {
      if (!it) continue;
      // Soft-deleted (DB-update reject) — skip so row loses its "Update"
      // annotation after Reject.
      if (it.deletedAt) continue;
      // ZY-10356 Phase 6 — include new-entry rejects for strikethrough.
      const isCommitted = it.state === "added_in_call" || it.state === "edited_in_call";
      const isNewReject = it.state === "rejected" && !it.dbRecordRef && it.lastCommitted;
      if (!isCommitted && !isNewReject) continue;
      if (it.operation === "add") stagedAddRows.push(stagedAddToMedRow(it));
    }
  }
  // Staged adds always land in Active (status defaults to "ongoing").
  const active = inTranscriptMode ? [...activeRaw, ...stagedAddRows] : activeRaw;
  const past = pastRaw;

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading && !doc) {
    return <div className="ph-slot-empty"><p>Loading medicines…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="med3-col">
      <div className="med3-stack">
        {/* ── Drug-allergies bar (3507:2860). Hidden when the patient has
            none: a permanent empty warning bar reads as an alert about
            nothing. ── */}
        {saltAllergies.length > 0 && (
          <div className="med3-allergy-bar">
            <div className="med3-allergy-left">
              <span className="med3-allergy-title">Salt allergies</span>
              <span className="med3-allergy-list">
                {saltAllergies.map((a) => a.name).join(" · ")}
              </span>
            </div>
            <button
              type="button"
              className="med3-allergy-link"
              onClick={() => EventService.emit(SECTION_SELECT, "allergies")}
            >
              View allergies <OpenInNewIcon />
            </button>
          </div>
        )}

        {/* ── Hero card (2964:4174) ── */}
        <div className="med3-card">
          <header className="med3-toolbar">
            <h2 className="med3-title">Medicines</h2>
            {view === VIEW_NEW ? (
              <span className="med3-count">
                &middot; {active.length === 0 ? "No entries" : `${active.length} entries`}
              </span>
            ) : null}
            <div className="med3-actions">
              {/* The same pill the anthropometric editor uses for cm / ft-in
                  (AnthropometricEditor.js:70-85), not a new control. */}
              {!inTranscriptMode ? (
                <div className="an-unit-pill" role="group" aria-label="Medicines view">
                {[VIEW_NEW, VIEW_OLD].map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`an-unit-pill-btn${v === view ? " is-on" : ""}`}
                    aria-pressed={v === view}
                    onClick={() => setView(v)}
                  >
                    {v}
                  </button>
                  ))}
                </div>
              ) : null}
              {/* Hidden while OLD is showing (history, nothing to add to).
                  Also hidden when the active list is empty — the empty-state
                  CTA below is the single, obvious next action; a header
                  duplicate would compete with it (mirrors CC/Dx). The header
                  button reappears the moment there is at least one active
                  row to sit alongside. */}
              {view === VIEW_NEW && active.length > 0 ? (
                <button type="button" className="med3-add" onClick={openAdd}>
                  <PlusIcon /> Add medicine
                </button>
              ) : null}
            </div>
          </header>

          {view === VIEW_OLD ? (
            <OldMedicines patientId={patientId} />
          ) : null}

          {view === VIEW_OLD ? null : active.length === 0 ? (
            <MedicinesEmpty
              title={past.length === 0 ? "No medicines recorded" : "No active medicines"}
              body={
                past.length === 0 ? (
                  <>
                    No medicines added yet. Add the patient&rsquo;s first medicine
                    to begin building their history.
                  </>
                ) : (
                  <>
                    All courses are completed or discontinued — find them in
                    Past medicines below. Add a medicine when a new one is
                    prescribed.
                  </>
                )
              }
            >
              <button type="button" className="med3-empty-add" onClick={openAdd}>
                <PlusIcon /> Add medicine
              </button>
            </MedicinesEmpty>
          ) : (
            <div className="med3-table" role="table">
              <div className="med3-thead" role="row">
                <span role="columnheader">Medicine</span>
                <span role="columnheader">Frequency (Dose qty.)</span>
                <span role="columnheader">Instructions</span>
                <span role="columnheader">Status</span>
                <span role="columnheader">Analysis</span>
              </div>

              {active.map((m) => {
                // Transcript mode disables updates on PERSISTED Med v2
                // rows (cases #5-#8) — this phase only supports new-add
                // (#1-#4). Staged-add rows (user's own captured, marked
                // _stagedAdd) stay clickable so the reviewer can tweak
                // their new entry before Save & next. Existing DB rows
                // render the name + status chip as plain text / inert
                // chips so there's no hyperlink to the (disabled)
                // update flow.
                const isReadonlyRow = inTranscriptMode && !m._stagedAdd;
                return (
                <div
                  className={`med3-row${m._captured ? " is-captured" : ""}${m._capturedIsAi ? " is-captured-ai" : ""}${m._isRejected ? " is-captured-rejected" : ""}`}
                  role="row"
                  key={m.medicineId}
                >
                  <div className="med3-line1">
                    <span className="med3-cell med3-c-name" role="cell">
                      {/* ZY-10356 Phase 6 — chip-above-name per mock. */}
                      {m._captured ? (
                        <span className={`zy-chip-captured${m._capturedIsAi ? " is-ai" : ""}${m._captured.operation === "update" ? " is-update" : ""}`}>
                          {m._capturedIsAi ? <span className="zy-chip-spark" aria-hidden="true">&#10022;</span> : null}
                          {m._captured.operation === "update" ? "Update" : "New"}
                        </span>
                      ) : null}
                      {/* Medicine name — a button when the update flow
                          is live, a plain span in transcript mode on an
                          existing DB row (updates disabled for this
                          phase). Mirrors Hospitalisation's "Cause of
                          hospitalisation" column. */}
                      {isReadonlyRow ? (
                        <span className="med3-name-btn is-readonly">
                          <MedicineName
                            medicine={m.medicine}
                            formulation={m.formulation}
                            formulationName={m.formulationName}
                          />
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="med3-name-btn"
                          onClick={() => openStatus(m)}
                          title="Update medicine"
                        >
                          <MedicineName
                            medicine={m.medicine}
                            formulation={m.formulation}
                            formulationName={m.formulationName}
                          />
                        </button>
                      )}
                    </span>
                    <span className="med3-cell med3-c-freqdose" role="cell">{frequencyDoseText(m)}</span>
                    <span className="med3-cell" role="cell">
                      {(m.current && INSTR_LABELS[m.current.instructions]) ||
                        (m.current && m.current.instructions) ||
                        "—"}
                    </span>
                    <span className="med3-cell med3-c-status" role="cell">
                      {/* Inert chip when the update flow is disabled —
                          renders the band + label but no click handler
                          + no caret, so there's no visual affordance
                          suggesting a drop-down pick. */}
                      {isReadonlyRow ? (
                        <StatusChip status={m.status} readOnly />
                      ) : (
                        <StatusChip status={m.status} onClick={() => openStatus(m)} />
                      )}
                    </span>
                    <span className="med3-cell med3-c-analysis" role="cell">
                      {hasTrend(m) ? (
                        <button
                          type="button"
                          className="med3-trend"
                          onClick={() => setTrendTarget(m)}
                        >
                          View trend
                        </button>
                      ) : (
                        <span className="med3-dash">&mdash;</span>
                      )}
                      <AdherenceButton medicine={m} onOpen={openAdherence} />
                    </span>
                  </div>
                  {/* Meta / description sits under Medicine + Frequency
                      only — the trailing columns (Instructions, Status,
                      Analysis) stay pristine so their chips don't drift
                      behind the wrapping description. Two-line clamp;
                      hover reveals the full text via portalled tooltip. */}
                  <div className="med3-line2">
                    <MetaTooltip text={metaText(m)} />
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Past-medicines bar (2964:4265) ── */}
      <div className={`med3-past${view === VIEW_OLD ? " is-hidden" : ""}`}>
        <button
          type="button"
          className="med3-past-bar"
          aria-expanded={pastOpen}
          onClick={() => setPastOpen((o) => !o)}
        >
          <span className="med3-past-left">
            <span className="med3-past-title">Past medicines</span>
            <span className="med3-past-count">
              {past.length} {past.length === 1 ? "entry" : "entries"}
            </span>
          </span>
          <span className={`med3-past-caret${pastOpen ? " is-open" : ""}`} aria-hidden="true">
            <ChipCaret />
          </span>
        </button>

        {pastOpen && past.length > 0 && (
          <div className="med3-card med3-past-card">
            <div className="med3-table" role="table">
              <div className="med3-thead med3-thead-past" role="row">
                <span role="columnheader">Medicine</span>
                <span role="columnheader">Started</span>
                <span role="columnheader">Description</span>
                <span role="columnheader">Ended on</span>
                <span role="columnheader">Status</span>
                <span role="columnheader">Analysis</span>
              </div>
              {past.map((m) => {
                // Past medicines are always persisted DB rows; transcript
                // mode disables updates on them (same rule as the active
                // table — only new adds supported this phase).
                const isReadonlyRow = inTranscriptMode;
                return (
                <div className="med3-row is-past" role="row" key={m.medicineId}>
                  <div className="med3-line1 med3-line1-past">
                    <span className="med3-cell med3-c-name" role="cell">
                      {/* Medicine name — button when the update flow is
                          live; plain span in transcript mode. */}
                      {isReadonlyRow ? (
                        <span className="med3-name-btn is-readonly">
                          <MedicineName
                            medicine={m.medicine}
                            formulation={m.formulation}
                            formulationName={m.formulationName}
                          />
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="med3-name-btn"
                          onClick={() => openStatus(m)}
                          title="Update medicine"
                        >
                          <MedicineName
                            medicine={m.medicine}
                            formulation={m.formulation}
                            formulationName={m.formulationName}
                          />
                        </button>
                      )}
                    </span>
                    <span className="med3-cell" role="cell">
                      {formatShortDate(m.current && m.current.medStartDate)}
                    </span>
                    <span className="med3-cell" role="cell">
                      {readMore ? (
                        <MobileReadMore>{(m.current && m.current.description) || "—"}</MobileReadMore>
                      ) : (
                        // Shared with the Active table's description cell —
                        // 2-line clamp + hover reveals the full text in a
                        // portalled white-bg / near-black popover (see
                        // MetaTooltip.js + `.med3-meta-pop`). Only fires
                        // when the clamp is actually eliding text; a
                        // short description or the "—" fallback renders
                        // without the hover affordance.
                        <MetaTooltip text={(m.current && m.current.description) || ""} />
                      )}
                    </span>
                    <span className="med3-cell" role="cell">
                      {(() => {
                        const d = endedSinceWhen(m);
                        return d ? formatShortDate(d) : "—";
                      })()}
                    </span>
                    <span className="med3-cell med3-c-status" role="cell">
                      {isReadonlyRow ? (
                        <StatusChip status={m.status} readOnly />
                      ) : (
                        <StatusChip status={m.status} onClick={() => openStatus(m)} />
                      )}
                    </span>
                    <span className="med3-cell med3-c-analysis" role="cell">
                      {hasTrend(m) ? (
                        <button
                          type="button"
                          className="med3-trend"
                          onClick={() => setTrendTarget(m)}
                        >
                          View trend
                        </button>
                      ) : (
                        <span className="med3-dash">&mdash;</span>
                      )}
                      <AdherenceButton medicine={m} onOpen={openAdherence} />
                    </span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {trendTarget && (
        <TrendModal
          patientId={patientId}
          medicine={trendTarget}
          onClose={() => setTrendTarget(null)}
        />
      )}
    </div>
  );
};

Medicines.propTypes = {
  /** Open the add form once the list loads — Create Rx's "+ Add …" (useAutoOpenAdd). */
  autoOpenAdd: PropTypes.bool,
  onAutoOpenAdd: PropTypes.func,
  patient: PropTypes.object,
  /**
   * Transcript-mode overlay. When populated, staged captured items for
   * `medicines` are merged into the active table (peach highlight +
   * sparkle for AI-touched rows). Empty / unpassed = standalone
   * behaviour, table shows only Med v2 server data. Wave A first-cut
   * merges only staged ADDS — update auto-routing lands in a follow-up.
   */
  capturedItems: PropTypes.array,
  /** Transcript-mode: fires with the latest {active ∪ past} rows list
   *  on every Med v2 fetch so CapturedSections can detect AI proposals
   *  that match an existing medicine (by name) and open them as
   *  update cards. */
  onRowsSync: PropTypes.func,
};

Medicines.defaultProps = {
  capturedItems: [],
  onRowsSync: null,
};

export default Medicines;
